import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { InlineTransactions } from './ClerkTransactions';
import ManageBookingsInline from '../components/ManageBookingsInline';
import Maintenance from './Maintenance';
import Security from './Security';
import { FaUserCog, FaCalendarAlt } from 'react-icons/fa';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

function Calendar({ selected, onSelect }) {
  const [month, setMonth] = useState(() => {
    const d = selected ? new Date(selected) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => {
    if (selected) setMonth(new Date(selected.getFullYear(), selected.getMonth(), 1));
  }, [selected]);

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  function daysInMonth(date) {
    const y = date.getFullYear();
    const m = date.getMonth();
    return new Date(y, m + 1, 0).getDate();
  }

  const firstDay = month.getDay();
  const totalDays = daysInMonth(month);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));

  function prev() { setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1)); }
  function next() { setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1)); }

  const today = new Date();

  return (
    <div className="inline-calendar">
      <div className="cal-header">
        <button type="button" className="cal-nav" onClick={prev} aria-label="Previous month">‹</button>
        <div className="cal-title">{month.toLocaleString(undefined, { month: 'long', year: 'numeric' })}</div>
        <button type="button" className="cal-nav" onClick={next} aria-label="Next month">›</button>
      </div>

      <div className="cal-grid">
        {weekdays.map(w => <div key={w} className="cal-weekday">{w}</div>)}
        {cells.map((dt, i) => (
          dt ? (
            <button
              key={i}
              type="button"
              className={`cal-day ${selected && new Date(selected).toDateString() === dt.toDateString() ? 'cal-day--selected' : ''} ${dt.toDateString() === today.toDateString() ? 'cal-day--today' : ''}`}
              onClick={() => onSelect(new Date(dt))}
            >
              {dt.getDate()}
            </button>
          ) : (
            <div key={i} className="cal-day cal-day--empty" />
          )
        ))}
      </div>
    </div>
  );
}

/* -------------------------
   Inline Issues (Clerk)
   ------------------------- */
function InlineIssues({ onClose }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subject, setSubject] = useState('Broken Seat');
  const [description, setDescription] = useState('');

  useEffect(() => {
    let mounted = true;
    async function fetchIssues() {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/clerk/issues');
        const data = Array.isArray(res.data) ? res.data : (res.data && res.data.data ? res.data.data : []);
        if (mounted) setIssues(data || []);
      } catch (e) {
        if (mounted) setError('Failed to load issues');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchIssues();
    return () => { mounted = false; };
  }, []);

  async function createIssue(e) {
    e && e.preventDefault && e.preventDefault();
    if (!description.trim()) return setError('Describe the issue');
    setError(null);
    try {
      const payload = { subject, description };
      const res = await api.post('/clerk/issues', payload).catch(() => null);
      if (res && res.data) {
        setIssues(prev => [res.data, ...prev]);
      } else {
        setIssues(prev => [{ id: Date.now(), subject, description, status: 'open' }, ...prev]);
      }
      setDescription('');
    } catch (err) {
      setError('Failed to create issue');
    }
  }

  return (
    <div className="p-4 bg-white rounded shadow mt-4">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-semibold">Issues</h4>
        <div className="flex items-center gap-2">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>

      <form onSubmit={createIssue} className="space-y-2 mb-3">
        <div className="flex gap-2">
          <input className="flex-1 border p-2 rounded" value={subject} onChange={e => setSubject(e.target.value)} />
          <button className="btn btn-primary" type="submit">Report</button>
        </div>
        <textarea className="w-full border p-2 rounded" rows={3} placeholder="Describe the issue" value={description} onChange={e => setDescription(e.target.value)} />
        {error && <div className="text-red-600">{error}</div>}
      </form>

      {loading && <div className="text-sm text-gray-600">Loading issues…</div>}
      {!loading && issues.length === 0 && <div className="text-sm text-gray-600">No issues</div>}

      <ul className="space-y-2">
        {issues.map(it => (
          <li key={it.id} className="p-2 border rounded flex justify-between items-center">
            <div>
              <div className="font-medium">#{it.id} — {it.subject || it.title || 'Issue'}</div>
              <div className="text-xs text-gray-500">Status: {it.status || 'open'}</div>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-sm btn-outline" onClick={() => window.location.assign('/clerk/issues')}>Open</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------
   Stat card and quick summary
   ------------------------- */
function StatCard({ label, value, icon, color }) {
  return (
    <div className="stat-card" style={{ background: color || 'linear-gradient(90deg,#eef2ff,#f0f9ff)', color: '#fff' }}>
      <div className="stat-inner">
        <div className="stat-icon">{icon}</div>
        <div style={{ marginLeft: 8 }}>
          <div className="text-2xl font-bold stat-value">{value}</div>
          <div className="text-sm stat-label">{label}</div>
        </div>
      </div>
    </div>
  );
}

function QuickFinancialSummary() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/reports/financial').then(r => {
      if (!mounted) return;
      setSummary(r.data || null);
    }).catch(() => { if (mounted) setSummary(null); });
    return () => { mounted = false; };
  }, []);

  if (!summary) return <div className="text-sm text-gray-600">No data</div>;

  const profitMargin = 0.35;
  const byEvent = (summary.byEvent || []).slice(0, 8);
  const chartData = byEvent.map(ev => {
    const revenue = Number(ev.revenue || 0);
    const profit = Math.round(revenue * profitMargin);
    return { name: ev.name || `#${ev.id}`, revenue, profit };
  });

  const totalRevenue = (summary.summary && summary.summary.totalRevenue) || summary.totalRevenue || 0;
  const totalBookings = (summary.summary && summary.summary.bookingsCount) || summary.bookingsCount || 0;
  const totalEstimatedProfit = Math.round(totalRevenue * profitMargin);

  return (
    <div className="flex flex-col sm:flex-row gap-6">
      <div style={{ flex: 1, minWidth: 220 }}>
        <div className="text-sm text-gray-500">Total Revenue</div>
        <div className="text-xl font-bold">₹{totalRevenue}</div>
        <div className="text-sm muted">Bookings: {totalBookings}</div>
        <div style={{ height: 8 }} />
        <div className="text-sm text-gray-500">Estimated Profit (assumed {Math.round(profitMargin * 100)}%)</div>
        <div className="text-lg font-semibold">₹{totalEstimatedProfit}</div>
      </div>

      <div style={{ flex: 1, minWidth: 260 }}>
        <div className="text-sm text-gray-500">Revenue & Estimated Profit by Event</div>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <XAxis type="number" stroke="#94a3b8" />
              <YAxis dataKey="name" type="category" width={140} stroke="#475569" />
              <Tooltip formatter={(v) => [`₹${v}`, 'Amount']} />
              <Legend />
              <Bar dataKey="revenue" fill="#06b6d4" name="Revenue" />
              <Bar dataKey="profit" fill="#10b981" name="Est. Profit" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <div className="text-sm text-gray-500">Pending Payments</div>
        <div className="text-xl font-bold">₹{summary.pendingAmount || 0}</div>
      </div>
    </div>
  );
}

/* -------------------------
   Recent bookings (Clerk)
   ------------------------- */
function RecentBookingsList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.get('/clerk/bookings?limit=5').then(r => {
      if (!mounted) return;
      const data = Array.isArray(r.data) ? r.data : (r.data && r.data.data ? r.data.data : []);
      setList((data || []).slice(0, 5));
    }).catch(() => {
      if (!mounted) return;
      setList([]);
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) return <div className="text-sm text-gray-600">Loading recent bookings...</div>;
  if (!list.length) return <div className="text-sm text-gray-600">No recent bookings</div>;

  return (
    <div className="recent-bookings">
      <ul className="space-y-2">
        {list.map(b => (
          <li key={b.id} className="flex justify-between items-center border-b pb-2">
            <div>
              <div className="text-sm font-medium">#{b.id} — {b.event_name || b.event}</div>
              <div className="text-xs text-gray-500">Seat: {b.seat || b.seat_no || '-'} • {b.status}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-sm">{b.amount ? `₹${b.amount}` : '-'}</div>
              <button className="btn btn-sm btn-ghost" onClick={() => navigate('/clerk/bookings?id=' + b.id)}>Manage</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------
   Small placeholders for Admin / Default roles
   ------------------------- */
const AdminDashboard = () => (
  <div className="p-6 bg-purple-100 rounded-lg shadow-lg border border-purple-200 relative">
    <FaUserCog className="text-purple-400 text-5xl absolute top-6 right-6 opacity-50" />
    <h3 className="text-2xl font-semibold mb-4 text-purple-900">Admin Dashboard</h3>
    <p className="text-purple-800">Admin tools: user management, financial reports and settings.</p>
  </div>
);

const DefaultDashboard = () => (
  <div className="p-6 bg-green-100 rounded-lg shadow-lg border border-green-200 relative">
    <FaCalendarAlt className="text-green-400 text-5xl absolute top-6 right-6 opacity-50" />
    <h3 className="text-2xl font-semibold mb-4 text-green-900">Welcome</h3>
    <p className="text-green-800">Public landing / overview for non-specialized roles.</p>
  </div>
);

/* -------------------------
   Main Dashboard
   ------------------------- */
export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // shared state
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // manager form
  const [form, setForm] = useState({ name: '', description: '', date: null, time: '', ticket_price: '' });
  const [creating, setCreating] = useState(false);
  const [showCalendarInline, setShowCalendarInline] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // clerk quick actions
  const [activeQuick, setActiveQuick] = useState(null);

  function generateTimeOptions() {
    const opts = [];
    for (let h = 6; h <= 23; h++) {
      for (const m of [0, 30]) {
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        const value = `${hh}:${mm}:00`;
        const hour12 = ((h + 11) % 12) + 1;
        const ampm = h < 12 ? 'AM' : 'PM';
        const label = `${hour12}:${mm} ${ampm}`;
        opts.push({ value, label });
      }
    }
    return opts;
  }
  const timeOptions = generateTimeOptions();

  const fetchMyEvents = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get('/events');
      setEvents(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.userMessage || e.message || 'Failed to load events');
      setEvents([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMyEvents(); }, [fetchMyEvents]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.name || !form.date || !form.time) return;
    setCreating(true);
    try {
      const dateStr = form.date ? (form.date instanceof Date ? form.date.toISOString().split('T')[0] : form.date) : form.date;
      const payload = { 
        name: form.name, 
        description: form.description, 
        date: dateStr, 
        time: form.time,
        ticket_price: form.ticket_price ? parseFloat(form.ticket_price) : 0
      };
      const r = await api.post('/events', payload);
      const created = r.data;
      setEvents(prev => [created, ...prev]);
      setForm({ name: '', description: '', date: null, time: '', ticket_price: '' });
      setShowCalendarInline(false);
    } catch (e) {
      setError(e.userMessage || e.message || 'Create event failed');
    } finally { setCreating(false); }
  }

  function openEvent(ev) {
    setSelectedEvent(ev);
    setShowModal(true);
  }
  function closeModal() { setSelectedEvent(null); setShowModal(false); }

  const stats = {
    total: events.length,
    pending: events.filter(e => (e.status || '').toLowerCase() === 'pending').length,
    approved: events.filter(e => (e.status || '').toLowerCase() === 'approved').length,
    rejected: events.filter(e => ['rejected', 'declined'].includes((e.status || '').toLowerCase())).length,
  };

  const featured = events.find(ev => ev.name && ev.name.toLowerCase() === 'autumn play');
  const orderedEvents = featured ? [featured, ...events.filter(e => e.id !== featured.id)] : events;

  function formatDateForInput(d) {
    if (!d) return '';
    try { return d instanceof Date ? d.toISOString().split('T')[0] : d; } catch { return ''; }
  }

  function RenderRoleDashboard() {
    switch (user?.role) {
      case 'Maintenance': return <Maintenance />;
      case 'Security': return <Security />;
      case 'Admin': return <AdminDashboard />;
      default: return <DefaultDashboard />;
    }
  }

  return (
    <div className="p-6 dashboard-page" style={{
      background: user?.role === 'Security' ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)' : 'linear-gradient(180deg,#f8fafc 0%, #eef2ff 100%)',
      color: user?.role === 'Security' ? '#f1f5f9' : '#0f172a',
      fontFamily: 'Poppins, system-ui, Arial',
      margin: 0,
      padding: user?.role === 'Security' ? '2rem' : '1.5rem',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <header className="dashboard-hero p-6 rounded-lg mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl hero-title">Dashboard</h1>
          <p className="text-gray-700 hero-sub">Welcome back{user?.name ? `, ${user.name}` : ''} — role: {user?.role || 'Guest'}</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="user-chip">{user?.name} <span className="user-role">{user?.role}</span></div>
          <button className="btn btn-ghost" onClick={() => navigate('/profile')}>Profile</button>
        </div>
      </header>

      {user?.role === 'Clerk' ? (
        <div className="space-y-6">
          <div className="p-4 bg-white rounded shadow">
            <h3 className="text-lg font-semibold mb-2">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <button
                className={activeQuick === 'bookings' ? 'btn btn-primary' : 'btn btn-outline'}
                onClick={() => setActiveQuick(a => a === 'bookings' ? null : 'bookings')}
              >
                Manage Bookings
              </button>

              <button
                className={activeQuick === 'transactions' ? 'btn btn-primary' : 'btn btn-outline'}
                onClick={() => setActiveQuick(a => a === 'transactions' ? null : 'transactions')}
              >
                Transactions
              </button>

              <button
                className={activeQuick === 'issues' ? 'btn btn-primary' : 'btn btn-outline'}
                onClick={() => setActiveQuick(a => a === 'issues' ? null : 'issues')}
              >
                Issues
              </button>
            </div>
          </div>

          {activeQuick === 'bookings' && <ManageBookingsInline onClose={() => setActiveQuick(null)} />}
          {activeQuick === 'transactions' && <InlineTransactions onClose={() => setActiveQuick(null)} />}
          {activeQuick === 'issues' && <InlineIssues onClose={() => setActiveQuick(null)} />}

          <div className="p-4 bg-white rounded shadow">
            <h3 className="text-lg font-semibold mb-2">Recent Bookings</h3>
            <RecentBookingsList />
          </div>
        </div>
      ) : user?.role === 'Manager' ? (
        <div className="space-y-6">
          <div className="stat-grid">
            <StatCard label="My Events" value={stats.total} icon="🎭" color="var(--accent1)" />
            <StatCard label="Pending" value={stats.pending} icon="⏳" color="var(--accent2)" />
            <StatCard label="Approved" value={stats.approved} icon="✅" color="var(--accent3)" />
            <StatCard label="Rejected" value={stats.rejected} icon="⛔" color="#f43f5e" />
          </div>

          <form onSubmit={handleCreate} className="card form-card">
            <div className="form-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 className="text-lg font-semibold">🎫 Create Event</h3>
                <div className="muted">Quickly add a show with date & time</div>
              </div>
              <div>
                <button className="btn btn-outline" type="button" onClick={() => setForm({ name: '', description: '', date: null, time: '', ticket_price: '' })}>Reset</button>
              </div>
            </div>

            {error && <div className="text-red-600">{error}</div>}

            <div className="form-grid" style={{ marginTop: 12 }}>
              <input
                className="flex-1 border p-3 rounded-lg shadow-sm"
                placeholder="Event name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
              />

              <div className="date-col" style={{ position: 'relative' }}>
                <input
                  readOnly
                  className="w-full border p-3 rounded-lg shadow-sm cursor-pointer"
                  placeholder="Select date"
                  value={formatDateForInput(form.date)}
                  onClick={() => setShowCalendarInline(s => !s)}
                />
                {showCalendarInline && (
                  <div className="cal-pop" style={{ position: 'absolute', top: 52, left: 0, zIndex: 40 }}>
                    <Calendar selected={form.date} onSelect={(d) => { setForm(f => ({ ...f, date: d })); setShowCalendarInline(false); }} />
                  </div>
                )}
              </div>

              <div>
                <select
                  className="w-full border p-3 rounded-lg shadow-sm"
                  value={form.time}
                  onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                  required
                >
                  <option value="">Select time</option>
                  {timeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div>
                <input
                  type="number"
                  className="w-full border p-3 rounded-lg shadow-sm"
                  placeholder="Ticket Price (₹)"
                  value={form.ticket_price}
                  onChange={e => setForm(f => ({ ...f, ticket_price: e.target.value }))}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <textarea className="w-full border p-3 rounded-lg shadow-sm" placeholder="Description" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ marginTop: 12 }} />

            <div className="form-actions" style={{ marginTop: 12 }}>
              <button className="btn btn-primary" disabled={creating}>{creating ? 'Creating...' : 'Create Event'}</button>
              <button type="button" className="btn btn-outline" onClick={() => setForm({ name: '', description: '', date: null, time: '', ticket_price: '' })} style={{ marginLeft: 8 }}>Clear</button>
            </div>
          </form>

          {featured && (
            <div className="p-4 bg-gradient-to-r from-yellow-50 to-indigo-50 rounded shadow flex gap-4 items-center">
              <div className="w-36 h-24 rounded overflow-hidden" style={{ backgroundImage: `url(/images/auditorium1.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <div>
                <div className="text-sm text-gray-500">Featured</div>
                <div className="font-bold text-lg">{featured.name} <span className="text-sm text-gray-500">{featured.date} {featured.time}</span></div>
                <div className="text-sm text-gray-700">{featured.description}</div>
                <div className="mt-2">
                  <button className="btn btn-primary mr-2" onClick={() => { navigator.clipboard?.writeText(window.location.origin + '/events/' + featured.id); }}>Share</button>
                  <button className="btn btn-outline" onClick={() => navigate('/bookings?event=' + featured.id)}>View Bookings</button>
                </div>
              </div>
            </div>
          )}

          <section>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">My Events
                <button aria-label="Refresh events" title="Refresh" onClick={fetchMyEvents} style={{ marginLeft: 8, background: 'transparent', border: 'none', cursor: 'pointer' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 12a9 9 0 10-3.1 6.5" stroke="#374151" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 3v6h-6" stroke="#374151" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </h3>
              <div className="flex gap-2">
                <button className="btn btn-sm btn-ghost" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Top</button>
              </div>
            </div>

            {loading ? (
              <div className="p-4 bg-white rounded shadow">Loading events...</div>
            ) : events.length === 0 ? (
              <div className="p-4 bg-white rounded shadow">You have no events yet.</div>
            ) : (
              <div className="my-events-grid events-grid">
                {orderedEvents.map((ev, idx) => (
                  <article key={ev.id || idx} className="event-card" onClick={() => openEvent(ev)} title="Click for details">
                    <div className="event-thumb" style={{ backgroundImage: `url(/images/auditorium${(idx % 3) + 1}.jpg)` }}>
                      <div className={`status-badge status-${(ev.status || 'Pending').toLowerCase()}`}>{ev.status || 'Pending'}</div>
                    </div>

                    <div className="event-body">
                      <h4 className="event-title">{ev.name}</h4>
                      <p className="text-sm event-desc">{ev.description}</p>

                      <div className="event-meta">
                        <div className="text-sm text-gray-600">{ev.date} • {ev.time}</div>
                        <div className="event-actions">
                          <button className="btn btn-sm btn-outline" onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(window.location.origin + '/events/' + ev.id); }}>Copy Link</button>
                          <button className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); navigate('/bookings?event=' + ev.id); }}>Bookings</button>
                        </div>
                      </div>

                      <div className="interest-row">
                        <div className="interest-label">Interest</div>
                        <div className="interest-bar"><div className="interest-fill" style={{ width: `${Math.min(90, ((ev.id || 1) % 10) * 10 + 30)}%` }} /></div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <div className="p-4 bg-white rounded shadow">
            <h3 className="font-semibold mb-2">Quick Financial Summary</h3>
            <QuickFinancialSummary />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="col-span-1 lg:col-span-3">
            <RenderRoleDashboard />
          </div>
        </div>
      )}

      {showModal && selectedEvent && (
        <div className="modal-overlay" onClick={closeModal} role="dialog" aria-modal="true">
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-image" style={{ backgroundImage: `url(/images/auditorium1.jpg)` }} />
            <div className="modal-body">
              <h3 className="text-xl font-bold">{selectedEvent.name} <span className="text-sm text-gray-500">#{selectedEvent.id}</span></h3>
              <p className="text-sm text-gray-700 mb-3">{selectedEvent.description}</p>
              <div className="text-sm text-gray-600 mb-3">When: {selectedEvent.date} {selectedEvent.time}</div>

              <div className="flex gap-2 mt-3">
                <button className="btn btn-primary" onClick={() => { navigator.clipboard?.writeText(window.location.origin + '/events/' + selectedEvent.id); }}>Copy Link</button>
                <button className="btn btn-outline" onClick={() => { navigate('/bookings?event=' + selectedEvent.id); }}>View Bookings</button>
                <button className="btn btn-ghost" onClick={closeModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}